import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { ModelConfig } from '@/types/misc';

export interface AppConfig {
  id: number;
  maintenance_mode: boolean;
  tagline: string;
  logo_url: string | null;
  privacy_policy: string;
  terms_of_service: string;
}

interface AppConfigContextType {
  config: AppConfig | null;
  parametricModels: ModelConfig[];
  creativeModels: ModelConfig[];
  isLoading: boolean;
  refreshConfig: () => Promise<void>;
}

const AppConfigContext = createContext<AppConfigContextType | undefined>(undefined);

export function AppConfigProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [parametricModels, setParametricModels] = useState<ModelConfig[]>([]);
  const [creativeModels, setCreativeModels] = useState<ModelConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchConfig = async () => {
    try {
      const [configRes, modelsRes] = await Promise.all([
        supabase.from('app_config').select('*').eq('id', 1).single(),
        // @ts-ignore - app_models is a new table and types haven't been regenerated
        supabase.from('app_models').select('*').eq('is_active', true),
      ]);
      
      if (configRes.error) throw configRes.error;
      if (modelsRes.error) throw modelsRes.error;
      
      setConfig(configRes.data as unknown as AppConfig);
      
      const models = modelsRes.data || [];
      const pModels = models.filter((m: any) => m.category === 'parametric').map((m: any) => ({
        id: m.id,
        name: m.name,
        description: m.description,
        provider: m.provider,
        supportsTools: m.supports_tools,
        supportsThinking: m.supports_thinking,
        supportsVision: m.supports_vision,
      }));
      const cModels = models.filter((m: any) => m.category === 'creative').map((m: any) => ({
        id: m.id,
        name: m.name,
        description: m.description,
        timeEstimate: m.time_estimate,
      }));
      
      setParametricModels(pModels);
      setCreativeModels(cModels);
    } catch (error) {
      console.error('Error fetching app configuration:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void fetchConfig();
  }, []);

  return (
    <AppConfigContext.Provider value={{ config, parametricModels, creativeModels, isLoading, refreshConfig: fetchConfig }}>
      {children}
    </AppConfigContext.Provider>
  );
}

export function useAppConfig() {
  const context = useContext(AppConfigContext);
  if (context === undefined) {
    throw new Error('useAppConfig must be used within an AppConfigProvider');
  }
  return context;
}
